import React from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Paper,
  Avatar,
  Fade,
  useTheme,
} from '@mui/material';
import {
  School as SchoolIcon,
  People as PeopleIcon,
  // Deprecated modules:
  // Search as SearchIcon,
  // Build as BuildIcon,
  CreditCard as CreditCardIcon,
  Email as EmailIcon,
  ArrowForward as ArrowForwardIcon,
  CalendarToday as CalendarIcon,
  // DeleteForever as DeleteForeverIcon,
  Traffic as TrafficIcon,
  Calculate as CalculateIcon,
  MenuBook as MenuBookIcon,
  ReceiptLong as ReceiptLongIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { gsap } from 'gsap';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    canAccessPrerequisites,
    canAccessAttendance,
    // Deprecated modules:
    // canAccessCourses,
    canAccessCourseBuilder,
    canAccessTempCards,
    canAccessTestSheetSubmissions,
    canAccessDOBVerifier,
    canManageUsers,
    isAdmin,
  } = usePermissions();
  const theme = useTheme();
  const cardRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  const canAccessAdminTools = () => canManageUsers() || isAdmin();

  React.useEffect(() => {
    // Animate cards on mount
    cardRefs.current.forEach((card, index) => {
      if (card) {
        gsap.fromTo(card,
          {
            opacity: 0,
            y: 50,
            scale: 0.9
          },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.6,
            delay: index * 0.2,
            ease: "back.out(1.7)"
          }
        );
      }
    });

    // Animate welcome text
    gsap.fromTo('.welcome-text',
      { opacity: 0, y: -30 },
      { opacity: 1, y: 0, duration: 0.8, ease: "power2.out" }
    );
  }, []);

  const allApps = [
    {
      title: 'Check Prerequisites',
      description: 'Verify student awards and prerequisites for course eligibility',
      icon: <SchoolIcon sx={{ fontSize: 40 }} />,
      path: '/award-check',
      color: theme.palette.primary.main,
      features: ['Award verification', 'Course eligibility', 'Student records'],
      canAccess: canAccessPrerequisites
    },
    {
      title: 'Attendance',
      description: 'Manage class attendance and student information',
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      path: '/attendance',
      color: theme.palette.secondary.main,
      features: ['Class management', 'Student details', 'Classroom splitting', 'Acuity Integration'],
      canAccess: canAccessAttendance
    },
    /* Deprecated: Verify Posted Courses
    {
      title: 'Verify Posted Courses',
      description: 'Scrape and verify course information from external sources',
      icon: <SearchIcon sx={{ fontSize: 40 }} />,
      path: '/course-scraper',
      color: theme.palette.success.main,
      features: ['Course verification', 'Data scraping', 'Schedule validation'],
      canAccess: canAccessCourses
    },
    */
    /* Deprecated: Course Builder
    {
      title: 'Course Builder',
      description: 'Create and manage classrooms, courses, and sessions for Salesforce upload',
      icon: <BuildIcon sx={{ fontSize: 40 }} />,
      path: '/course-upload',
      color: theme.palette.warning.main,
      features: ['Classroom creation', 'Course management', 'Session scheduling', 'Salesforce integration'],
      canAccess: canAccessCourseBuilder
    },
    */
    {
      title: 'Course Profitability',
      description: 'Model course scenarios, locations, instructor costs, and profitability',
      icon: <CalculateIcon sx={{ fontSize: 40 }} />,
      path: '/course-profitability',
      color: '#00897b',
      features: ['Scenario planning', 'Pool and classroom costs', 'Instructor rates', 'Profit comparison'],
      canAccess: canAccessCourseBuilder
    },
    {
      title: 'Temp Cards',
      description: 'Generate temporary certification cards',
      icon: <CreditCardIcon sx={{ fontSize: 40 }} />,
      path: '/temp-cards',
      color: theme.palette.info.main,
      features: ['Certificate generation', 'PDF download', 'Multiple cert types'],
      canAccess: canAccessTempCards
    },
    {
      title: 'Test Sheet Submissions',
              description: 'Monitor test sheet submissions sent to and from test_sheets@lifeguarding.com',
      icon: <EmailIcon sx={{ fontSize: 40 }} />,
      path: '/test-sheet-submissions',
      color: '#9c27b0',
      features: ['Multi-account monitoring', 'Email attachments', 'Smart classification', 'Export data'],
      canAccess: canAccessTestSheetSubmissions
    },
    {
      title: 'DOB Verifier',
      description: 'Verify student dates of birth and calculate ages for course eligibility',
      icon: <CalendarIcon sx={{ fontSize: 40 }} />,
      path: '/dob-verifier',
      color: theme.palette.error.main,
      features: ['Age verification', 'Date of birth lookup', 'Course eligibility check', 'CSV export'],
      canAccess: canAccessDOBVerifier
    },
    /* Deprecated: Object Purge
    {
      title: 'Object Purge',
      description: '⚠️ DANGER: Permanently delete ALL records from a Salesforce object',
      icon: <DeleteForeverIcon sx={{ fontSize: 40 }} />,
      path: '/object-purge',
      color: '#b71c1c', // Dark red to indicate danger
      features: ['⚠️ Complete data deletion', 'Dependency analysis', 'Safety confirmations', 'Admin audit trails'],
      canAccess: canManageUsers // Admin-only access
    },
    */
    {
      title: 'Road Closures',
      description: 'See critical Toronto weekend travel disruptions before classes and events',
      icon: <TrafficIcon sx={{ fontSize: 40 }} />,
      path: '/road-closures',
      color: '#ef6c00',
      features: ['Critical travel highlights', 'Weekend closures', 'Facility impact checks'],
      canAccess: canManageUsers // Admin-only access
    },
    {
      title: 'Company Policies',
      description: 'Search policies, process docs, related links, and knowledge graph connections',
      icon: <MenuBookIcon sx={{ fontSize: 40 }} />,
      path: '/company-policies',
      color: '#27445C',
      features: ['Policy search', 'Google Drive sync', 'Related policy graph', 'AI-ready retrieval'],
      canAccess: canManageUsers // Admin-only access
    },
    {
      title: 'LSS Invoices',
      description: 'Monitor outstanding LSS invoices and alert admins when the total crosses the threshold',
      icon: <ReceiptLongIcon sx={{ fontSize: 40 }} />,
      path: '/lss-invoices',
      color: '#546e7a',
      features: ['Outstanding total', 'Asana alert', 'Largest invoices', 'Scheduler-ready'],
      canAccess: canAccessAdminTools
    }
  ];

  // Filter apps based on user permissions
  const apps = allApps.filter(app => app.canAccess());

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Fade in timeout={800}>
        <Box>
          {/* Welcome Header */}
          <Paper
            elevation={3}
            sx={{
              p: 4,
              mb: 4,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}10, ${theme.palette.secondary.main}10)`,
              borderRadius: 3
            }}
          >
            <Box className="welcome-text" sx={{ textAlign: 'center' }}>
              <Typography variant="h3" component="h1" sx={{ mb: 2, fontWeight: 300 }}>
                Hi {user?.first_name}
              </Typography>

              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
                Choose from the applications below to get started.
              </Typography>
            </Box>
          </Paper>

          {/* App Cards */}
          {apps.length > 0 ? (
            <Grid container spacing={4}>
              {apps.map((app, index) => (
                <Grid
                  item
                  xs={12}
                  md={apps.length === 1 ? 12 : apps.length === 2 ? 6 : 4}
                  key={app.title}
                >
                <Card
                  ref={(el) => cardRefs.current[index] = el}
                  elevation={4}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 8,
                    },
                    '&:hover .app-icon': {
                      transform: 'scale(1.1) rotate(5deg)',
                    },
                    '&:hover .arrow-icon': {
                      transform: 'translateX(4px)',
                    }
                  }}
                  onClick={() => handleNavigate(app.path)}
                >
                  {/* Decorative Background */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: 100,
                      height: 100,
                      background: `linear-gradient(135deg, ${app.color}20, ${app.color}10)`,
                      borderRadius: '0 0 0 100%',
                    }}
                  />

                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar
                        className="app-icon"
                        sx={{
                          bgcolor: app.color,
                          width: 60,
                          height: 60,
                          mr: 2,
                          transition: 'all 0.3s ease'
                        }}
                      >
                        {app.icon}
                      </Avatar>
                      <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
                        {app.title}
                      </Typography>
                    </Box>

                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3, mt: 3, ml: 1, mr: 1, lineHeight: 1.6 }}>
                      {app.description}
                    </Typography>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, ml: 1, mr: 1, color: app.color, fontWeight: 600 }}>
                        Key Features:
                      </Typography>
                      {app.features.map((feature, featureIndex) => (
                        <Typography
                          key={featureIndex}
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 0.5,
                            '&:before': {
                              content: '"•"',
                              color: app.color,
                              mr: 1,
                              ml: 1,
                              fontWeight: 'bold'
                            }
                          }}
                        >
                          {feature}
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>

                  <CardActions sx={{ p: 3, pt: 0 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      endIcon={
                        <ArrowForwardIcon
                          className="arrow-icon"
                          sx={{ transition: 'transform 0.3s ease' }}
                        />
                      }
                      sx={{
                        bgcolor: app.color,
                        '&:hover': {
                          bgcolor: app.color,
                          filter: 'brightness(1.1)',
                        },
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontSize: '1rem',
                        fontWeight: 600
                      }}
                    >
                      Open {app.title}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
              ))}
            </Grid>
          ) : (
            <Paper
              elevation={2}
              sx={{
                p: 4,
                textAlign: 'center',
                background: `linear-gradient(45deg, ${theme.palette.warning.light}20, ${theme.palette.warning.main}10)`,
                borderRadius: 3,
                border: `1px solid ${theme.palette.warning.light}`
              }}
            >
              <Typography variant="h5" sx={{ mb: 2, color: 'text.primary' }}>
                No Applications Available
              </Typography>
              <Typography variant="body1" color="text.secondary">
                You don't have access to any applications yet. Please contact your administrator to get the necessary permissions.
              </Typography>
            </Paper>
          )}

          {/* Quick Stats or Additional Info */}
          <Paper
            elevation={2}
            sx={{
              mt: 4,
              p: 3,
              textAlign: 'center',
              background: `linear-gradient(45deg, ${theme.palette.grey[50]}, ${theme.palette.grey[100]})`,
              borderRadius: 2
            }}
          >
            <Typography variant="h6" sx={{ mb: 1, color: 'text.primary' }}>
              Need Help?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Contact your system administrator or refer to the documentation for assistance with any of these tools.
            </Typography>
          </Paper>
        </Box>
      </Fade>
    </Container>
  );
};

export default Home;
